import { Button } from '@/components/ui/button.jsx'
import { Icon } from '@iconify/react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.jsx'
import { motion } from 'framer-motion'
import { handleError } from '@/services/errorHandler.js'
import { toast } from 'sonner'
import * as API from '@/services/api.js'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace.jsx'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AnalyticsService } from '@/services/analytics.js'
import { NewReportDialog } from '@/layouts/dashboard/nav/subnavs/NewReportDialog.jsx'
import { ApproveDeleteDialog } from '@/components/dialogs/ApproveDeleteDialog.jsx'
import { DashboardsNavSection } from '@/layouts/dashboard/nav/subnavs/DashboardsNavSection.jsx'
import { useViewsListContext } from '@/context-state/ViewsListContext.jsx'
import { DEFAULT_REPORTS, DEFAULT_DASHBOARD } from '@/layouts/dashboard/nav/subnavs/_defaults.jsx'
import { TypographySmall } from '@/components/ui/typography.jsx'
import { UserRoles } from '@/consts/index.js'
import { EntitlementsKeys } from '@/guards/EntitlementsGuard.jsx'
import { useFreeTierFence } from '@/hooks/useFreeTierFence.jsx'
import { getReportIcon, DEFAULT_REPORT_ICON_COLOR } from '@/consts/reportIcons.js'
import { NavItemIcon } from '@/layouts/dashboard/nav/subnavs/NavItemIcon.jsx'
import { usePersistedState } from '@/hooks/usePersistedState.jsx'
import { RoleGuard } from '@/guards/RoleGuard.jsx'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input.jsx'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu.jsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip.jsx'
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const DEFAULT_FOLDER_ID = 'default'

// Sortable and droppable folder component
const SortableFolder = ({ id, children, isOver }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: `folder-sort-${id}` })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`transition-colors ${isOver ? 'bg-primary/5 rounded-lg' : ''} ${isDragging ? 'z-50' : ''}`}
        >
            {children({ dragHandleProps: { ...attributes, ...listeners }, isDragging })}
        </div>
    )
}

// Droppable folder component (for receiving reports)
const DroppableFolder = ({ id, children }) => {
    const { setNodeRef, isOver } = useDroppable({ id })
    return (
        <div ref={setNodeRef} className={`transition-colors ${isOver ? 'bg-primary/5 rounded-lg' : ''}`}>
            {children}
        </div>
    )
}

// Sortable report item component
const SortableReportItem = ({ report, isChosen, onNavigate, onRename, onIconChange, onDuplicate, onDelete, folders, currentFolderId, onMoveToFolder, index }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: report._id })

    const [isRenaming, setIsRenaming] = useState(false)
    const [draft, setDraft] = useState(report.title || '')

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    const displayTitle = report.title

    const commitRename = () => {
        const next = draft.trim()
        setIsRenaming(false)
        if (next && next !== report.title) {
            onRename(report._id, next)
        } else {
            setDraft(report.title || '')
        }
    }

    const startRename = () => {
        setDraft(report.title || '')
        setIsRenaming(true)
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-1 relative rounded-lg overflow-hidden group/report ${isChosen ? 'bg-primary/10 text-primary' : 'hover:bg-accent'} ${isDragging ? 'z-50' : ''}`}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1"
            >
                <Icon icon="ph:dots-six-vertical" className="h-4 w-4 text-muted-foreground opacity-30 group-hover/report:opacity-100 transition-opacity" />
            </div>

            <NavItemIcon
                icon={report.icon}
                defaultIcon={getReportIcon(report.type)}
                defaultClassName={DEFAULT_REPORT_ICON_COLOR}
                onChange={(nextIcon) => onIconChange(report._id, nextIcon)}
            />

            {isRenaming ? (
                <Input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            commitRename()
                        }
                        if (e.key === 'Escape') {
                            setIsRenaming(false)
                            setDraft(report.title || '')
                        }
                    }}
                    onBlur={commitRename}
                    className="h-7 flex-1 mr-2 !text-[13px]"
                />
            ) : (
                <>
                    <TooltipProvider delayDuration={400}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div
                                    className="flex-1 cursor-pointer py-1.5 pr-1"
                                    onClick={() => onNavigate(report._id, displayTitle)}
                                >
                                    <TypographySmall className="font-light truncate max-w-[110px] !text-[13px]">
                                        {displayTitle}
                                    </TypographySmall>
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                <p>{displayTitle}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>

                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 opacity-0 group-hover/report:opacity-100 transition-opacity mr-1"
                            >
                                <Icon icon="ph:dots-three-vertical" className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={startRename}>
                                <Icon icon="ph:pencil-simple" className="h-4 w-4 mr-2 flex-shrink-0" />
                                Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onDuplicate(report._id)}>
                                <Icon icon="ph:copy" className="h-4 w-4 mr-2 flex-shrink-0" />
                                Duplicate
                            </DropdownMenuItem>
                            {folders.length > 1 && (
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>
                                        <Icon icon="stash:folder-light" className="h-4 w-4 mr-2 flex-shrink-0" />
                                        Move to folder
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                        {folders.map((f) => (
                                            <DropdownMenuItem
                                                key={f._id}
                                                onClick={() => onMoveToFolder(report._id, f._id)}
                                                className={currentFolderId === f._id ? 'bg-accent' : ''}
                                            >
                                                <Icon icon="stash:folder-light" className="h-4 w-4 mr-2 flex-shrink-0" />
                                                <span className="truncate max-w-[120px]">{f.name}</span>
                                                {currentFolderId === f._id && (
                                                    <Icon icon="ph:check" className="h-4 w-4 flex-shrink-0 ml-1" />
                                                )}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => onDelete(report)} className="text-destructive focus:text-destructive">
                                <Icon icon="ph:trash" className="h-4 w-4 mr-2 flex-shrink-0" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            )}
        </div>
    )
}

// Drag overlay component for reports
const DragOverlayItem = ({ report }) => {
    if (!report) return null

    return (
        <div className="flex items-center gap-2 bg-white shadow-xl rounded-lg px-2 py-2 border border-primary/20">
            <Icon icon="ph:dots-six-vertical" className="h-4 w-4 text-muted-foreground" />
            <Icon icon={getReportIcon(report.type)} className="h-4 w-4 text-secondary" />
            <TypographySmall className="font-light">{report.title}</TypographySmall>
        </div>
    )
}

// Drag overlay component for folders
const FolderDragOverlayItem = ({ folder }) => {
    if (!folder) return null

    return (
        <div className="flex items-center gap-2 bg-white shadow-xl rounded-lg px-3 py-2 border border-primary/20">
            <Icon icon="ph:dots-six-vertical" className="h-4 w-4 text-muted-foreground" />
            <Icon icon="stash:folder-light" className="h-4 w-4" />
            <TypographySmall className="font-light">{folder.name}</TypographySmall>
        </div>
    )
}


export const CreativeAnalyticsSubNavbar = ({ showTopLinks = true }) => {

    const { creativeContext, adAccountsContext } = useDashboardContext()
    const { addManyCreativeReportsToState, creativeReports, updateCreativeReportState, updateManyCreativeReportsState, removeCreativeReportFromState, reportFolders, addReportFolderToState, updateReportFolderState, updateManyReportFoldersState, removeReportFolderFromState, updateReportFoldersOrder } = creativeContext
    const [searchParams, setSearchParams] = useSearchParams()
    const { chosenAdAccount } = adAccountsContext
    const navigate = useNavigate()
    const { accountId, spaceId } = useParams()
    const { views: spaceViews, isFetched: spaceViewsFetched, createView: createViewInList } = useViewsListContext()
    const { space } = useCurrentSpace()
    const seededViewsRef = useRef(false)
    const seededReportsRef = useRef(false)
    const location = useLocation()
    const superSegmentFromLocationState = location.state?.superSegment;
    const chosenReportId = searchParams.get('report')
    const superSegmentFromSearchParams = searchParams.get('superSegment')
    const [isNewReportDialogOpen, setIsNewReportDialogOpen] = useState(false)
    // Free tier keeps its default report(s) but can't create new ones.
    const { enforce: enforceCreateReport } = useFreeTierFence(EntitlementsKeys.REPORTS_MANAGE, 'free-reports-limit', 'reports_create')
    const pathname = window.location.pathname

    // Folder state - combines default folder with backend folders
    // Backend folders are fetched via context (reportFolders)
    const folders = [
        { _id: DEFAULT_FOLDER_ID, name: 'No folder', isDefault: true },
        ...reportFolders.map(f => ({ ...f, _id: f._id, isDefault: false }))
    ].sort((a, b) => {
        // Default folder always first
        if (a.isDefault) return -1
        if (b.isDefault) return 1
        // Then sort by order if exists
        return (a.order ?? 0) - (b.order ?? 0)
    })
    const [isAddingFolder, setIsAddingFolder] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')
    const [isReportsOpen, setIsReportsOpen] = usePersistedState('sidebar:reports:open', true)
    const [editingFolderId, setEditingFolderId] = useState(null)
    const [editingFolderName, setEditingFolderName] = useState('')
    const [openFolders, setOpenFolders] = usePersistedState('sidebar:reports:openFolders', [])
    const [reportToDelete, setReportToDelete] = useState(null)
    // Currently dragging report or folder
    const [activeReport, setActiveReport] = useState(null)
    const [activeFolder, setActiveFolder] = useState(null)

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    )

    // Helper function to resolve report's folder ID (handles orphaned reports)
    const getReportFolderId = (report) => {
        if (!report?.folderId) return DEFAULT_FOLDER_ID
        const folderExists = folders.some(f => f._id === report.folderId)
        return folderExists ? report.folderId : DEFAULT_FOLDER_ID
    }

    const handleDragStart = (event) => {
        const { active } = event
        const activeId = active.id

        // Check if dragging a folder
        if (typeof activeId === 'string' && activeId.startsWith('folder-sort-')) {
            const folderId = activeId.replace('folder-sort-', '')
            const folder = folders.find(f => f._id === folderId)
            setActiveFolder(folder)
            setActiveReport(null)
            return
        }

        // Dragging a report
        const report = creativeReports.find(r => r._id === activeId)
        setActiveReport(report)
        setActiveFolder(null)
    }

    const handleDragEnd = async (event) => {
        const { active, over } = event

        // Handle folder reordering
        if (activeFolder) {
            setActiveFolder(null)
            if (!over) return

            const activeId = active.id.replace('folder-sort-', '')
            const overId = over.id.replace('folder-sort-', '')

            if (activeId !== overId) {
                const oldIndex = folders.findIndex(f => f._id === activeId)
                const newIndex = folders.findIndex(f => f._id === overId)

                if (oldIndex !== -1 && newIndex !== -1) {
                    // Get only backend folders (exclude default) and reorder them
                    const backendFolders = folders.filter(f => !f.isDefault)
                    const activeBackendIndex = backendFolders.findIndex(f => f._id === activeId)
                    const overBackendIndex = backendFolders.findIndex(f => f._id === overId)

                    if (activeBackendIndex !== -1 && overBackendIndex !== -1) {
                        const newOrderedFolders = arrayMove(backendFolders, activeBackendIndex, overBackendIndex)

                        // Update ALL folders with their new order (0-based index)
                        const foldersWithNewOrder = newOrderedFolders.map((folder, i) => ({
                            ...folder,
                            order: i
                        }))

                        // Update all folders in state at once (optimistic update)
                        updateManyReportFoldersState(foldersWithNewOrder)

                        // Then make API calls in parallel for ALL folders
                        Promise.all(
                            foldersWithNewOrder.map(folder =>
                                API.updateFolder(folder._id, { order: folder.order }).catch(handleError)
                            )
                        )
                    }
                }
            }
            return
        }

        // Handle report dragging
        setActiveReport(null)

        if (!over) return

        const activeId = active.id
        const overId = over.id

        // Skip if dropped on folder sort handles
        if (typeof overId === 'string' && overId.startsWith('folder-sort-')) return

        // Check if dropped on a folder
        const targetFolder = folders.find(f => f._id === overId)
        if (targetFolder) {
            // Move report to this folder
            moveReportToFolder(activeId, targetFolder._id)
            return
        }

        // Check if reordering within the same folder or dropped on another report
        const activeReport = creativeReports.find(r => r._id === activeId)
        const overReport = creativeReports.find(r => r._id === overId)

        const activeReportFolderId = getReportFolderId(activeReport)
        const overReportFolderId = getReportFolderId(overReport)

        if (activeReportFolderId === overReportFolderId) {
            // Reordering within the same folder
            const folderReports = getOrderedReportsForFolder(activeReportFolderId)
            const oldIndex = folderReports.findIndex(r => r._id === activeId)
            const newIndex = folderReports.findIndex(r => r._id === overId)

            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                const newOrderedReports = arrayMove(folderReports, oldIndex, newIndex)

                // Update ALL reports with their new order (1-based index)
                const reportsWithNewOrder = newOrderedReports.map((report, i) => ({
                    ...report,
                    order: i + 1
                }))

                // Update all reports in state at once (optimistic update)
                updateManyCreativeReportsState(reportsWithNewOrder)

                // Then make API calls in parallel for ALL reports
                Promise.all(
                    reportsWithNewOrder.map(report =>
                        API.updateCreativeReport(report._id, { order: report.order }).catch(handleError)
                    )
                )
            }
        } else {
            // Moving to a different folder (dropped on a report in another folder)
            moveReportToFolder(activeId, overReportFolderId)
        }
    }

    const handleDragOver = (event) => {
        const { active, over } = event
        if (!over) return

        // Skip drag over handling for folders
        if (activeFolder) return

        const activeId = active.id
        const overId = over.id

        // Don't do anything if hovering over the same item
        if (activeId === overId) return

        // Skip if over a folder sort handle
        if (typeof overId === 'string' && overId.startsWith('folder-sort-')) return

        const activeReport = creativeReports.find(r => r._id === activeId)
        const activeReportFolderId = getReportFolderId(activeReport)

        // Check if over is a folder
        const targetFolder = folders.find(f => f._id === overId)
        if (targetFolder) return // Let handleDragEnd handle folder drops

        // Check if over is a report in a different folder
        const overReport = creativeReports.find(r => r._id === overId)
        const overReportFolderId = getReportFolderId(overReport)

        if (activeReportFolderId !== overReportFolderId) {
            // Moving to a different folder
            moveReportToFolder(activeId, overReportFolderId)
        }
    }

    const addNewFolder = async () => {
        if (!newFolderName.trim()) {
            setIsAddingFolder(false)
            return
        }
        try {
            const { data: newFolder } = await API.createFolder({ name: newFolderName.trim() })
            addReportFolderToState(newFolder)
            setNewFolderName('')
            setIsAddingFolder(false)
            toast.success('Folder created')
        } catch (e) {
            toast.error('Failed to create folder')
            handleError(e)
        }
    }

    const renameFolder = async (folderId) => {
        if (!editingFolderName.trim()) {
            setEditingFolderId(null)
            return
        }
        try {
            const { data: updatedFolder } = await API.updateFolder(folderId, { name: editingFolderName.trim() })
            updateReportFolderState(updatedFolder)
            setEditingFolderId(null)
            setEditingFolderName('')
        } catch (e) {
            toast.error('Failed to rename folder')
            handleError(e)
        }
    }

    const deleteFolder = async (folderId) => {
        try {
            // First, move all reports in this folder to default folder (folderId: null) on the backend
            const reportsInFolder = creativeReports.filter(r => r.folderId === folderId)
            if (reportsInFolder.length > 0) {
                // Update reports on the backend
                await Promise.all(
                    reportsInFolder.map(report =>
                        API.updateCreativeReport(report._id, { folderId: null })
                    )
                )
                // Update local state
                const reportsWithNoFolder = reportsInFolder.map(report => ({ ...report, folderId: null }))
                updateManyCreativeReportsState(reportsWithNoFolder)
            }
            // Then delete the folder
            await API.deleteFolder(folderId)
            removeReportFolderFromState(folderId)
            toast.success('Folder deleted')
        } catch (e) {
            toast.error('Failed to delete folder')
            handleError(e)
        }
    }

    const moveReportToFolder = async (reportId, folderId) => {
        try {
            const newFolderId = folderId === DEFAULT_FOLDER_ID ? null : folderId
            const { data: updatedReport } = await API.updateCreativeReport(reportId, { folderId: newFolderId })
            updateCreativeReportState(updatedReport)
        } catch (e) {
            toast.error('Failed to move report')
            handleError(e)
        }
    }

    const renameReport = async (reportId, newTitle) => {
        try {
            const report = creativeReports.find(r => r._id === reportId)
            if (!report) {
                return
            }
            updateCreativeReportState({ ...report, title: newTitle })
            await API.updateCreativeReport(reportId, { title: newTitle })
            AnalyticsService.track('report-renamed', { reportId })
        } catch (e) {
            handleError(e)
            toast.error('Failed to rename report')
        }
    }

    const changeReportIcon = async (reportId, newIcon) => {
        try {
            const report = creativeReports.find(r => r._id === reportId)
            if (!report) {
                return
            }
            updateCreativeReportState({ ...report, icon: newIcon })
            await API.updateCreativeReport(reportId, { icon: newIcon })
            AnalyticsService.track('report-icon-changed', { reportId, icon: newIcon.value, color: newIcon.color })
        } catch (e) {
            handleError(e)
            toast.error('Failed to update icon')
        }
    }

    const duplicateReport = async (reportId) => {
        try {
            const report = creativeReports.find(r => r._id === reportId)
            if (!report) {
                return
            }
            const payload = { ...report, title: `${report.title} (Copy)`, order: creativeReports.length + 1 }
            delete payload._id
            delete payload.account
            delete payload.space
            delete payload.createdAt
            delete payload.updatedAt
            toggleLoader(true)
            const { data } = await API.createCreativeReport(payload)
            addManyCreativeReportsToState([data])
            navigate(`creative?report=${data._id}`)
            AnalyticsService.track('report-duplicated', { newReportId: data._id, originalReportId: reportId })
            toast.success('Report duplicated')
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        } finally {
            toggleLoader(false)
        }
    }

    const confirmDeleteReport = async () => {
        const target = reportToDelete
        setReportToDelete(null)
        if (!target) {
            return
        }
        if (creativeReports.length === 1) {
            toast.info('At least one report is required')
            return
        }
        try {
            toggleLoader(true)
            await API.deleteCreativeReport(target._id)
            removeCreativeReportFromState(target._id)
            AnalyticsService.track('report-deleted', { reportId: target._id })
            toast.success('Report deleted')
            if (chosenReportId === target._id) {
                const another = creativeReports.find(r => r._id !== target._id)
                navigate(another ? `creative?report=${another._id}` : 'creative/home')
            }
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        } finally {
            toggleLoader(false)
        }
    }

    const getReportsForFolder = (folderId) => {
        return creativeReports.filter(report => {
            // If report has no folderId or the folder doesn't exist, it belongs to the default folder
            if (!report.folderId) {
                return folderId === DEFAULT_FOLDER_ID
            }
            // Check if the folder exists
            const folderExists = folders.some(f => f._id === report.folderId)
            if (!folderExists) {
                // Orphaned report - show in default folder
                return folderId === DEFAULT_FOLDER_ID
            }
            return report.folderId === folderId
        })
    }

    const getOrderedReportsForFolder = (folderId) => {
        const folderReports = getReportsForFolder(folderId)
        // Sort by report.order field
        return folderReports.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    }

    const createDefaultReports = async (shouldNavigate = true) => {
        try {
            toggleLoader(true)
            // Seed the space's default reports from the shared defaults. Account-specific
            // fields (_id / account / space / timestamps) are stripped so the backend stamps
            // a fresh id + the current account/space.
            const allDefaultReports = await Promise.all(
                DEFAULT_REPORTS.map((report) => {
                    const payload = { ...report }
                    delete payload._id
                    delete payload.account
                    delete payload.space
                    delete payload.createdAt
                    delete payload.updatedAt
                    return API.createCreativeReport(payload).then(({ data }) => data)
                })
            )
            addManyCreativeReportsToState(allDefaultReports)
            if (shouldNavigate && allDefaultReports[0]) {
                navigate(`creative?report=${allDefaultReports[0]._id}`)
            }
        } catch (e) {
            handleError(e)
            console.error(e)
            toast.error(e.message)
        } finally {
            toggleLoader(false)
        }
    }

    // Seed the space's default dashboards (views) from the shared defaults. Drops the
    // source space's ids/account/space and leaves the ad account UNSET (empty scope +
    // null widget overrides) — the widgets fall back to the space's chosen ad account,
    // so the dashboards always follow whichever account is selected (set by the Views
    // layout's EnsureChosenAdAccount / the choose-account dialog).
    const createDefaultViews = async () => {
        try {
            await Promise.all(
                DEFAULT_DASHBOARD.map((view) => {
                    const payload = { ...view }
                    delete payload._id
                    delete payload.account
                    delete payload.space
                    delete payload.createdAt
                    delete payload.updatedAt
                    payload.scope = { ...(payload.scope || {}), adAccountIds: [], integrationIds: [] }
                    payload.widgets = (payload.widgets || []).map((w) => ({ ...w, override: null }))
                    return createViewInList(payload, { system: true })
                })
            )
        } catch (e) {
            handleError(e)
            toast.error(e.message)
        }
    }

    const createNewReportAndNavigateToIt = async (reportType) => {
        try {
            setIsNewReportDialogOpen(false)

            // Backstop for every create-report entry point (dialog, command bar).
            if (enforceCreateReport()) {
                return
            }

            if (!chosenAdAccount) {
                toast.error('Please select an ad account first')
                return
            }

            toggleLoader(true)

            let description = 'See which creatives are achieving the best performance'
            let title = 'New Top Performing Report'
            let chartType = 'compact'
            let icon = { type: 'icon', value: 'ph:medal', color: 'text-gray-500' }
            let segmentConfigs = {}

            if (reportType === 'custom-groups-comparison') {
                description = 'Evaluate multiple sets of creatives against each other by filtering through custom groups'
                title = 'Custom Groups Comparison Report'
                chartType = 'bar'
                icon = { type: 'icon', value: 'ph:stack', color: 'text-secondary' }
            }

            if (reportType === 'launch') {
                description = 'Track and analyze ads launched since a specific date with customizable segments'
                title = 'Launch Report'
                chartType = 'card'
                icon = { type: 'icon', value: 'ph:rocket', color: 'text-primary' }
                segmentConfigs = {
                    scaled: {
                        name: "Scaled",
                        groupFilters: [
                            [
                                {
                                    optionId: "metric",
                                    value: "500",
                                    operatorValue: "greater",
                                    label: "Metric",
                                    chosenMetric: {
                                        key: "spend",
                                        name: "Spend",
                                        color: "bg-gradient-to-r from-blue-500 to-teal-400",
                                        fromColor: "#3B82F6",
                                        toColor: "#2DD4BF",
                                        currency: true,
                                        percentages: false,
                                        description: "Total amount of money spent on the asset.",
                                        formula: "Sum of all expenses related to the asset.",
                                        bestValue: "high",
                                        isNatural: true,
                                        displaySum: true,
                                        displayAverage: true
                                    }
                                }
                            ]
                        ],
                        icon: {
                            type: "icon",
                            value: "ph:chart-line-up",
                            color: "text-gray-500",
                            bg: "bg-gray-500"
                        }
                    },
                    winners: {
                        name: "Winners",
                        groupFilters: [
                            [
                                {
                                    "optionId": "metric",
                                    "value": "500",
                                    "operatorValue": "greater",
                                    "label": "Metric",
                                    "chosenMetric": {
                                        "key": "spend",
                                        "name": "Spend",
                                        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
                                        "fromColor": "#3B82F6",
                                        "toColor": "#2DD4BF",
                                        "currency": true,
                                        "percentages": false,
                                        "description": "Total amount of money spent on the asset.",
                                        "formula": "Sum of all expenses related to the asset.",
                                        "bestValue": "high",
                                        "isNatural": true,
                                        "displaySum": true,
                                        "displayAverage": true
                                    }
                                }
                            ],
                            [
                                {
                                    "optionId": "metric",
                                    "value": "1",
                                    "operatorValue": "greater",
                                    "label": "Metric",
                                    "chosenMetric": {
                                        "key": "roas",
                                        "name": "ROAS",
                                        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
                                        "fromColor": "#A855F7",
                                        "toColor": "#EC4899",
                                        "currency": false,
                                        "percentages": false,
                                        "displaySum": false,
                                        "displayAverage": true,
                                        "bestValue": "high",
                                        "description": "Return on ad spend - a ratio of revenue generated for every dollar spent.",
                                        "formula": "Revenue / Spend",
                                        "dependsOn": [
                                            "spend",
                                            "purchase_value"
                                        ]
                                    }
                                }
                            ]
                        ],
                        icon: {
                            type: "icon",
                            value: "ph:trophy",
                            color: "text-secondary",
                            bg: "bg-secondary"
                        }
                    }
                }
            }


            const defaultReportProperties = {
                order: creativeReports ? creativeReports.length + 1 : 1,
                title,
                icon,
                type: reportType,
                description,
                chartType,
                segmentConfigs,
                metricKeys: ['spend', 'ctr', 'impressions'],
                filters: [],
                groupBy: {
                    key: "ad_name",
                    label: "Ad Name"
                }
            }

            const { data } = await API.createCreativeReport(defaultReportProperties)

            addManyCreativeReportsToState([data])
            // Newly created reports land at the root — just keep the Reports section open
            setIsReportsOpen(true)
            // Full-navigate to the new report unless we're already on the /creative report page
            // (where just swapping the ?report param is enough). Creative sub-pages and any
            // non-creative route (e.g. intelligence) both need a real navigation.
            if (pathname.includes('home') || pathname.includes('video-highlight') || pathname.includes('insights') || pathname.includes('account-overview') || pathname.includes('views') || !pathname.includes('creative')) {
                navigate(`creative?report=${data._id}`)
                return
            } else {
                setSearchParams({ report: data._id })
            }
            AnalyticsService.track('new-creative-report-created', { reportId: data._id })
            toast.success('Report created successfully')
        } catch (e) {
            handleError(e)
            console.error(e)
            toast.error(e.message)
        } finally {
            toggleLoader(false)
        }
    }

    const navigateToReport = (reportId, reportTitle) => {
        navigate(`creative?report=${reportId}`)
        AnalyticsService.track('selected-report-changed', { reportId, reportTitle })
    }

    // Seed the space's default reports when it has none — independent of route.
    // The subnav is always mounted in the single sidebar now, so this can't live
    // in the route-gated navigation effect below (the user may land first on the
    // Ads Library / a non-creative route after onboarding). DashboardDataProvider
    // blocks render until reports are fetched, so an empty list here is the true
    // server state, not a pre-fetch flash.
    useEffect(() => {
        if (!seededReportsRef.current && creativeReports.length === 0) {
            seededReportsRef.current = true
            createDefaultReports(false)
        }
    }, [creativeReports.length])

    useEffect(() => {
        // This sub-nav is now always mounted in the single sidebar (not just on
        // /creative). The report-routing below must only act on creative routes,
        // otherwise it would redirect users off intelligence/other pages to a report.
        if (!pathname.includes('creative')) {
            return
        }

        // Non-report creative pages (home, insights, views, …) don't pick a report.
        if (pathname.includes('insights') || pathname.includes('video-explorer') || pathname.includes('home') || pathname.includes('video-highlight') || pathname.includes('account-overview') || pathname.includes('views')) {
            return
        }

        // A shared-creative deep link (?creative=…) drives CreativePage itself.
        // Skip auto-selecting a default report here — navigating would replace the
        // query string and drop ?creative/&since/&until before the creative opens.
        if (searchParams.get('creative')) {
            return
        }

        // No reports yet (seeding in flight) — nothing to navigate to.
        const firstReport = creativeReports[0]
        if (!firstReport) {
            return
        }

        const superSegment = superSegmentFromLocationState || superSegmentFromSearchParams
        // Set the first report as default
        if (!chosenReportId) {
            navigate(`creative?report=${firstReport._id}${superSegment ? `&superSegment=${superSegment}` : ''}`)
            return
        }

        // Check it the report exists on the state (if the user switch spaces it won't be there)
        const reportExistsInCurrentState = creativeReports.find(report => report._id === chosenReportId)
        if (reportExistsInCurrentState) {
            navigate(`creative?report=${chosenReportId}${superSegment ? `&superSegment=${superSegment}` : ''}`)
            return
        }
        // The default - if the report doesn't exist in the state, navigate to the first report
        navigate(`creative?report=${firstReport._id}${superSegment ? `&superSegment=${superSegment}` : ''}`)

    }, [creativeReports, chosenReportId, pathname])

    // Keep the Reports section (and the report's folder, if any) open when a report is active.
    useEffect(() => {
        if (!chosenReportId) {
            return
        }
        const report = creativeReports.find(r => r._id === chosenReportId)
        if (!report) {
            return
        }
        setIsReportsOpen(true)
        const folderId = getReportFolderId(report)
        if (folderId !== DEFAULT_FOLDER_ID) {
            setOpenFolders(prev => prev.includes(folderId) ? prev : [...prev, folderId])
        }
    }, [chosenReportId, creativeReports])

    // Independent of reports: when the space has no dashboards (views) yet, seed the defaults.
    useEffect(() => {
        // Wait for the space's ad accounts to load before seeding, so the dashboards get a
        // default ad account (otherwise their widgets would have no account selected).
        if (spaceViewsFetched && spaceViews.length === 0 && !seededViewsRef.current && space?.adAccounts?.length) {
            seededViewsRef.current = true
            createDefaultViews()
        }
    }, [spaceViewsFetched, spaceViews.length, space?.adAccounts?.length])

    // Reset the seed guard when the space changes, so default dashboards get seeded
    // per-space (the views list itself resets on space change too).
    useEffect(() => {
        seededViewsRef.current = false
        seededReportsRef.current = false
    }, [spaceId])

    return (
        <div className={showTopLinks ? 'pt-4' : 'pt-1'}>

            <NewReportDialog
                isDialogOpen={isNewReportDialogOpen}
                setIsDialogOpen={setIsNewReportDialogOpen}
                onCreateNewReport={createNewReportAndNavigateToIt}
            />

            <ApproveDeleteDialog
                isDialogOpen={!!reportToDelete}
                onClose={() => setReportToDelete(null)}
                approveDelete={confirmDeleteReport}
                title="Delete report"
                subtitle={`Delete "${reportToDelete?.title || 'this report'}"? This can't be undone.`}
            />

            {showTopLinks && (
                <div className="px-3">
                    <div
                        className={`flex items-center gap-2 relative transition-all duration-200 cursor-pointer px-3 py-2.5 rounded-lg mb-1 ${pathname.includes('home') && !pathname.includes('account-overview') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                        onClick={() => navigate('creative/home')}
                    >
                        <Icon icon="ph:house" className="h-5 w-5" />
                        <span className="text-sm font-light">Home</span>
                    </div>

                    <div
                        className={`flex items-center gap-2 relative transition-all duration-200 cursor-pointer px-3 py-2.5 rounded-lg mb-1 ${pathname.includes('insights') ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                        onClick={() => navigate('creative/insights')}
                    >
                        <Icon icon="fluent:brain-sparkle-20-regular" className="h-5 w-5" />
                        <span className="text-sm font-light">Weekly Insights</span>
                    </div>

                    <Separator />
                </div>
            )}

            <div className="mt-1 flex flex-col pl-3">

                <DashboardsNavSection />

                <div className="flex items-center justify-between pr-2 mb-1 mt-3 flex-shrink-0">
                    <button
                        onClick={() => setIsReportsOpen(o => !o)}
                        className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <Icon icon="ph:caret-right" className={`h-3 w-3 transition-transform duration-200 ${isReportsOpen ? 'rotate-90' : ''}`} />
                        Reports
                    </button>
                    <RoleGuard minimumRoleRequired={UserRoles.EDITOR}>
                        <div className="flex items-center gap-0.5">
                            <Button
                                onClick={() => setIsAddingFolder(true)}
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 hover:bg-primary/10"
                                title="Add folder"
                            >
                                <Icon icon="quill:folder-add" className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (enforceCreateReport()) {
                                        return
                                    }
                                    setIsNewReportDialogOpen(true)
                                }}
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                data-intercom-target="create-new-report-button"
                                title="New report"
                            >
                                <Icon icon="ic:baseline-plus" className="h-6 w-6 rounded-full bg-primary/10 p-1 text-primary" />
                            </Button>
                        </div>
                    </RoleGuard>
                </div>

                {isReportsOpen && (
                    <>

                        {isAddingFolder && (
                            <div className="flex items-center gap-1 px-2 mb-2 flex-shrink-0 pr-3">
                                <Icon icon="stash:folder-light" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <Input
                                    autoFocus
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addNewFolder()
                                        if (e.key === 'Escape') {
                                            setIsAddingFolder(false)
                                            setNewFolderName('')
                                        }
                                    }}
                                    onBlur={addNewFolder}
                                    placeholder="Folder name..."
                                    className="h-7 text-sm"
                                />
                            </div>
                        )}

                        <div className="overflow-y-auto flex-1 pr-2">
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                onDragOver={handleDragOver}
                            >
                                {/* Root-level reports (no folder) — flat list, like dashboards */}
                                <DroppableFolder id={DEFAULT_FOLDER_ID}>
                                    <div className="space-y-0.5 px-2 pb-1">
                                        <SortableContext
                                            items={getOrderedReportsForFolder(DEFAULT_FOLDER_ID).map(r => r._id)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {getOrderedReportsForFolder(DEFAULT_FOLDER_ID).map((report, index) => (
                                                <motion.div
                                                    key={report._id}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.03 * index, duration: 0.2 }}
                                                >
                                                    <SortableReportItem
                                                        report={report}
                                                        index={index}
                                                        isChosen={chosenReportId === report._id}
                                                        onNavigate={navigateToReport}
                                                        onRename={renameReport}
                                                        onIconChange={changeReportIcon}
                                                        onDuplicate={duplicateReport}
                                                        onDelete={setReportToDelete}
                                                        folders={folders}
                                                        currentFolderId={DEFAULT_FOLDER_ID}
                                                        onMoveToFolder={moveReportToFolder}
                                                    />
                                                </motion.div>
                                            ))}
                                        </SortableContext>
                                    </div>
                                </DroppableFolder>

                                {/* Custom folders — collapsible accordions */}
                                <SortableContext
                                    items={folders.filter(f => !f.isDefault).map(f => `folder-sort-${f._id}`)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <Accordion
                                        type="multiple"
                                        value={openFolders}
                                        onValueChange={setOpenFolders}
                                        className="w-full"
                                    >
                                        {folders.filter(f => !f.isDefault).map((folder) => {
                                            const folderReports = getOrderedReportsForFolder(folder._id)

                                            return (
                                                <SortableFolder key={folder._id} id={folder._id}>
                                                    {({ dragHandleProps, isDragging }) => (
                                                        <AccordionItem value={folder._id} className={`border-0 ${isDragging ? 'opacity-50' : ''}`}>
                                                            <DroppableFolder id={folder._id}>
                                                                <div className="group">
                                                                    <AccordionTrigger className="pr-1 py-2 hover:no-underline">
                                                                        <div className="flex items-center justify-between w-full pr-2">
                                                                            <div className="flex items-center gap-1 flex-1 min-w-0">
                                                                                {folder.isDefault ? (
                                                                                    <div className="w-4" />
                                                                                ) : (
                                                                                    <div
                                                                                        {...dragHandleProps}
                                                                                        className="cursor-grab active:cursor-grabbing"
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    >
                                                                                        <Icon icon="ph:dots-six-vertical" className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                                    </div>
                                                                                )}
                                                                                <Icon icon="stash:folder-light" className="h-4 w-4 flex-shrink-0" />
                                                                                {editingFolderId === folder._id ? (
                                                                                    <Input
                                                                                        autoFocus
                                                                                        value={editingFolderName}
                                                                                        onChange={(e) => setEditingFolderName(e.target.value)}
                                                                                        onKeyDown={(e) => {
                                                                                            e.stopPropagation()
                                                                                            if (e.key === 'Enter') renameFolder(folder._id)
                                                                                            if (e.key === 'Escape') {
                                                                                                setEditingFolderId(null)
                                                                                                setEditingFolderName('')
                                                                                            }
                                                                                        }}
                                                                                        onBlur={() => renameFolder(folder._id)}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        className="h-6 text-sm py-0"
                                                                                    />
                                                                                ) : (
                                                                                    <TooltipProvider delayDuration={300}>
                                                                                        <Tooltip>
                                                                                            <TooltipTrigger asChild>
                                                                                                <div>
                                                                                                    <TypographySmall className="font-light truncate max-w-[100px]">{folder.name}</TypographySmall>
                                                                                                </div>
                                                                                            </TooltipTrigger>
                                                                                            <TooltipContent side="right">
                                                                                                <p>{folder.name}</p>
                                                                                            </TooltipContent>
                                                                                        </Tooltip>
                                                                                    </TooltipProvider>
                                                                                )}
                                                                                {/* {folderReports.length > 0 && ( */}
                                                                                {/* <span className="text-xs text-muted-foreground">({folderReports.length})</span> */}
                                                                                {/* )} */}
                                                                            </div>
                                                                            {!folder.isDefault && (
                                                                                <DropdownMenu>
                                                                                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="icon"
                                                                                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                                        >
                                                                                            <Icon icon="ph:dots-three" className="h-4 w-4" />
                                                                                        </Button>
                                                                                    </DropdownMenuTrigger>
                                                                                    <DropdownMenuContent align="end">
                                                                                        <DropdownMenuItem onClick={() => {
                                                                                            setEditingFolderId(folder._id)
                                                                                            setEditingFolderName(folder.name)
                                                                                        }}>
                                                                                            <Icon icon="ph:pencil" className="h-4 w-4 mr-2" />
                                                                                            Rename
                                                                                        </DropdownMenuItem>
                                                                                        <DropdownMenuItem
                                                                                            onClick={() => deleteFolder(folder._id)}
                                                                                            className="text-red-600"
                                                                                        >
                                                                                            <Icon icon="ph:trash" className="h-4 w-4 mr-2" />
                                                                                            Delete
                                                                                        </DropdownMenuItem>
                                                                                    </DropdownMenuContent>
                                                                                </DropdownMenu>
                                                                            )}
                                                                        </div>
                                                                    </AccordionTrigger>
                                                                </div>
                                                            </DroppableFolder>
                                                            <AccordionContent className="pb-2">
                                                                <div className="space-y-0.5 px-2 pl-2">
                                                                    {folderReports.length === 0 ? (
                                                                        <div className="text-xs text-muted-foreground px-2 py-2">
                                                                            No reports in this folder
                                                                        </div>
                                                                    ) : (
                                                                        <SortableContext
                                                                            items={folderReports.map(r => r._id)}
                                                                            strategy={verticalListSortingStrategy}
                                                                        >
                                                                            {folderReports.map((report, index) => (
                                                                                <motion.div
                                                                                    key={report._id}
                                                                                    initial={{ opacity: 0, x: -20 }}
                                                                                    animate={{ opacity: 1, x: 0 }}
                                                                                    transition={{ delay: 0.03 * index, duration: 0.2 }}
                                                                                >
                                                                                    <SortableReportItem
                                                                                        report={report}
                                                                                        index={index}
                                                                                        isChosen={chosenReportId === report._id}
                                                                                        onNavigate={navigateToReport}
                                                                                        onRename={renameReport}
                                                                                        onIconChange={changeReportIcon}
                                                                                        onDuplicate={duplicateReport}
                                                                                        onDelete={setReportToDelete}
                                                                                        folders={folders}
                                                                                        currentFolderId={folder._id}
                                                                                        onMoveToFolder={moveReportToFolder}
                                                                                    />
                                                                                </motion.div>
                                                                            ))}
                                                                        </SortableContext>
                                                                    )}
                                                                </div>
                                                            </AccordionContent>
                                                        </AccordionItem>
                                                    )}
                                                </SortableFolder>
                                            )
                                        })}
                                    </Accordion>
                                </SortableContext>
                                <DragOverlay dropAnimation={null}>
                                    {activeReport && <DragOverlayItem report={activeReport} />}
                                    {activeFolder && <FolderDragOverlayItem folder={activeFolder} />}
                                </DragOverlay>
                            </DndContext>
                        </div>
                    </>
                )}
            </div>


        </div>
    )
}